import { useEffect } from "react";
import { Button, Container, Form } from "react-bootstrap";
import clsx from "classnames";
import ArrowRight from "@/assets/ArrowRight.svg";
import classes from "./styles.module.scss";
import { useCallback, useState } from "react";
import { sector_helper } from "@/helpers/sector_helper";
import { job_type_helper } from "@/helpers/job_type_helper";
import { salary_helper } from "@/helpers/salary_helper";
import { jobs_helper } from "@/helpers/jobs_helper"; // Make sure this is the correct import path
import JobsResults from "./JobsResults";

export function QuickJobSearch({
  sectionId,
  className,
  title,
  titleColor,
  bgColor,
  showQuery,
  showSector,
  showLocation,
  showJobType,
  showSalary,
  showRadius,
  employerId,
  limit,
  loadOnInit,
}) {
  const [sectors] = useState(sector_helper.fetch());
  const [job_types] = useState(job_type_helper.fetch());
  const [salaries] = useState(salary_helper.fetch());
  const [resetFlag, setResetFlag] = useState(false);

  const [query, setQuery] = useState("");
  const [sector, setSector] = useState("");
  const [location, setLocation] = useState("");
  const [job_type, setJobType] = useState([]);
  const [salary, setSalary] = useState("");
  const [radius, setRadius] = useState("");
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [showJobs, setShowJobs] = useState(false);

  const slugify = (str) => str.toLowerCase().trim().replace(/\s+/g, "-");

  const handleSubmit = useCallback(() => {
    setHasSearched(true);
    setShowJobs(true);
    const results = jobs_helper.fetch({
      filter: (job) => {
        if (query && !job.title.toLowerCase().includes(query.toLowerCase())) return false;
        if (location && (!job.location || !job.location.toLowerCase().includes(location.toLowerCase()))) return false;
        if (sector && !JSON.stringify(job.categories).toLowerCase().includes(sector.toLowerCase())) return false;
        if (
          job_type.length > 0 &&
          !job_type.some((jt) => JSON.stringify(job.categories).toLowerCase().includes(jt.toLowerCase()))
        )
          return false;
        if (salary && !JSON.stringify(job.categories).toLowerCase().includes(salary.toLowerCase())) return false;

        if (Array.isArray(employerId) && employerId.length > 0) {
          const employerIds = jobs_helper.getCategoryValueIds("1df83e15-03f4-4ce7-9f8d-9b20d0ea1538", job);
          if (!employerId.some((id) => employerIds.includes(id))) return false;
        }

        return true;
      },
      limit: limit || null, // ✅ Respect the limit if provided
    });

    setFilteredJobs(results);
  }, [query, sector, location, job_type, salary, radius, employerId, limit]);

  const resetFilters = () => {
    setQuery("");
    setSector("");
    setLocation("");
    setJobType([]);
    setSalary("");
    setRadius("");
    setResetFlag(true); // 🚀 triggers the search after state is cleared
  };

  useEffect(() => {
    if (resetFlag) {
      handleSubmit();
      setResetFlag(false); // reset the flag
    }
  }, [resetFlag, handleSubmit]);

  useEffect(() => {
    if (loadOnInit) {
      handleSubmit(); // Auto-fetch on initial load only if true
    }
  }, [loadOnInit, handleSubmit]);

  return (
    <div id={sectionId} className={clsx(className, classes.search)}>
      <Container className="mw-xxl">
        <Form
          className={clsx(classes.search__form)}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className={classes.inner}>
            <h3 className={titleColor}>{title}</h3>
            <div className={clsx(classes.fields, bgColor)}>
              {showQuery && (
                <Form.Group controlId="formJobTitle">
                  <Form.Label>Job Title / Keywords</Form.Label>
                  <Form.Control
                    placeholder="E.g. LGV Driver"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </Form.Group>
              )}

              {showSector && (
                <Form.Group controlId="formSector">
                  <Form.Label>Sector</Form.Label>
                  <Form.Select value={sector} onChange={(e) => setSector(e.target.value)}>
                    <option value="">Select</option>
                    {sectors.map(({ parent, title }) =>
                      parent.identifier == null ? (
                        <option key={title} value={title}>
                          {title.replace(" Jobs", "")}
                        </option>
                      ) : null,
                    )}
                  </Form.Select>
                </Form.Group>
              )}

              {showLocation && (
                <Form.Group controlId="formLocation">
                  <Form.Label>Location / Postcode</Form.Label>
                  <Form.Control
                    placeholder="E.g. London"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  />
                </Form.Group>
              )}

              {showJobType && (
                <Form.Group controlId="formJobType">
                  <Form.Label>Job Type</Form.Label>
                  <Form.Select
                    value={job_type}
                    onChange={(e) => setJobType(Array.from(e.target.selectedOptions, (o) => o.value))}
                  >
                    {job_types.map(({ title }) => (
                      <option key={title} value={title}>
                        {title}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              {showSalary && (
                <Form.Group controlId="formSalary">
                  <Form.Label>Salary</Form.Label>
                  <Form.Select value={salary} onChange={(e) => setSalary(e.target.value)}>
                    <option value="">Select</option>
                    {salaries.map(({ annum }) => (
                      <option key={annum} value={annum}>
                        {annum}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              {showRadius && (
                <Form.Group controlId="formSearchRadius">
                  <Form.Label>Within</Form.Label>
                  <Form.Select value={radius} onChange={(e) => setRadius(e.target.value)}>
                    {[5, 10, 20, 30, 40, 50].map((m) => (
                      <option key={m} value={m}>
                        {m} miles
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              <div className={classes.searchBtnWrap}>
                <Button variant="secondary" type="submit" className={classes.searchBtn}>
                  Search Jobs <ArrowRight />
                </Button>
              </div>
            </div>
          </div>
        </Form>

        {/* Results Section */}
        {filteredJobs.length > 0 && showJobs ? (
          <div className={classes.results}>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
              <p className={classes.resultText}>Found {filteredJobs.length} job(s)</p>
              <div className="d-flex gap-2 align-items-center">
                <Button variant="outline-secondary" onClick={resetFilters}>
                  Reset Search
                </Button>
                <Button variant="outline-secondary" className={classes.closeBtn} onClick={() => setShowJobs(false)}>
                  &times;
                </Button>
              </div>
            </div>

            <JobsResults jobs={filteredJobs} />
          </div>
        ) : hasSearched && showJobs ? (
          <div className={classes.noResults}>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
              <p className={classes.resultText + " mb-0"}>No jobs found matching your search.</p>
              <div className="d-flex gap-2 align-items-center">
                <Button variant="outline-secondary" onClick={resetFilters}>
                  Reset Search
                </Button>
                <Button variant="outline-secondary" className={classes.closeBtn} onClick={() => setShowJobs(false)}>
                  &times;
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </Container>
    </div>
  );
}

export default QuickJobSearch;
